import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

import { PreviewHeader } from '../PreviewHeader';
import { AadhaarCardMock } from './AadhaarCardMock';

const INSTRUCTIONS = [
  'Use the FRONT side of the Aadhaar — the one with your photo, name and Aadhaar number.',
  'Fit the whole card inside the frame — all four corners visible.',
  'Good lighting, flat surface, no glare or shadows.',
  'Hold the camera steady so every detail is sharp and readable.',
];

// Aadhaar OCR — scan the Aadhaar card. Same scan frame + animated line as PAN's
// OCR screen; card + copy changed. `children` lets the session swap in a live
// camera/upload preview (builder shows the static card).
export function AadhaarOcrScreen({
  children,
  large = false,
}: {
  children?: ReactNode;
  large?: boolean;
}) {
  return (
    <>
      <PreviewHeader
        title="Aadhaar Verification"
        subtitle="Capture the front of your Aadhaar card"
      />

      <div className="relative mb-2 w-full overflow-hidden rounded-2xl border border-[#99B6FF] bg-[#E5EDFF] px-6 py-5">
        <div className="absolute left-3 top-3 z-20 h-4 w-4 border-l-[2.5px] border-t-[2.5px] border-[#0048FF]" />
        <div className="absolute right-3 top-3 z-20 h-4 w-4 border-r-[2.5px] border-t-[2.5px] border-[#0048FF]" />
        <div className="absolute bottom-2 left-3 z-20 h-4 w-4 border-b-[2.5px] border-l-[2.5px] border-[#0048FF]" />
        <div className="absolute bottom-2 right-3 z-20 h-4 w-4 border-b-[2.5px] border-r-[2.5px] border-[#0048FF]" />
        <div className="animate-scan-line absolute left-0 z-30 h-0.5 w-full bg-[#0048FF] shadow-[0_0_8px_2px_rgba(0,72,255,0.5)]" />
        {children ?? <AadhaarCardMock large={large} compact={!large} />}
      </div>

      <ul className="mb-2 flex flex-col gap-2.5">
        {INSTRUCTIONS.map((text) => (
          <li
            key={text}
            className="flex items-start gap-2 text-[length:var(--text-12)] leading-tight text-gray30"
          >
            <div className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full bg-primary text-white">
              <Check size={10} strokeWidth={4} />
            </div>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      <p className="mb-3 mt-2 text-[11px] leading-snug text-[#6B7280]">
        Your Aadhaar details are verified securely. We do not store your personal information beyond
        what is required by law.
      </p>
    </>
  );
}
